import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Loader2, Send, CheckCircle2, Linkedin, Github, Twitter, MessageCircle, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/common/SEO";
import PageHeader from "@/components/shared/PageHeader";
import { Reveal, Icon } from "@/components/common/motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { company, serviceOptions, budgetOptions, trustBadges } from "@/data/site";
import { submitContact } from "@/lib/api";
import { cn } from "@/lib/utils";

const empty = { name: "", company: "", email: "", phone: "", service: "", budget: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState("");

  const copy = (text, key) => {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(""), 1800);
  };

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Enter a valid email.";
    if (form.message.trim().length < 5) e.message = "Tell us a little more.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await submitContact(form);
      toast.success(res.message || "Message sent!");
      setDone(true);
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Contact" description="Get in touch with NeuroTriq TechLabs. Book a free consultation — we reply within one business day." path="/contact" />
      <PageHeader
        overline="Contact"
        title="Let's build something great together"
        subtitle="Tell us about your project or challenge. We'll get back to you within one business day."
      />

      <section className="py-20 sm:py-28">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[1fr_0.85fr]">
          {/* Form */}
          <Reveal>
            <div className="rounded-3xl border border-border bg-card p-7 sm:p-10">
              {done ? (
                <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="contact-success">
                  <span className="grid h-16 w-16 place-items-center rounded-full bg-success/15 text-success"><CheckCircle2 className="h-8 w-8" /></span>
                  <h2 className="mt-6 font-display text-2xl font-medium">Thank you!</h2>
                  <p className="mt-2 max-w-sm text-muted-foreground">Your message is on its way. Our team will reach out within one business day.</p>
                  <button onClick={() => setDone(false)} className="mt-8 rounded-full border border-border px-6 py-2.5 text-sm font-medium hover:border-primary/50" data-testid="contact-send-another">
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form" noValidate>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="grid gap-2">
                      <Label htmlFor="name">Full name *</Label>
                      <Input id="name" value={form.name} onChange={set("name")} placeholder="Jane Doe" data-testid="contact-name" aria-invalid={!!errors.name} />
                      {errors.name && <p className="text-xs text-destructive" data-testid="error-name">{errors.name}</p>}
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="company">Company</Label>
                      <Input id="company" value={form.company} onChange={set("company")} placeholder="Acme Inc." data-testid="contact-company" />
                    </div>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="grid gap-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input id="email" type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" data-testid="contact-email" aria-invalid={!!errors.email} />
                      {errors.email && <p className="text-xs text-destructive" data-testid="error-email">{errors.email}</p>}
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input id="phone" value={form.phone} onChange={set("phone")} placeholder="+1 (555) 000-0000" data-testid="contact-phone" />
                    </div>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="grid gap-2">
                      <Label>Service of interest</Label>
                      <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                        <SelectTrigger data-testid="contact-service"><SelectValue placeholder="Select a service" /></SelectTrigger>
                        <SelectContent>
                          {serviceOptions.map((s) => <SelectItem key={s} value={s} data-testid={`service-opt-${s}`}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label>Budget range</Label>
                      <Select value={form.budget} onValueChange={(v) => setForm((f) => ({ ...f, budget: v }))}>
                        <SelectTrigger data-testid="contact-budget"><SelectValue placeholder="Select a range" /></SelectTrigger>
                        <SelectContent>
                          {budgetOptions.map((b) => <SelectItem key={b} value={b} data-testid={`budget-opt-${b}`}>{b}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea id="message" rows={5} value={form.message} onChange={set("message")} placeholder="Tell us about your project, goals and timeline..." data-testid="contact-message" aria-invalid={!!errors.message} />
                    {errors.message && <p className="text-xs text-destructive" data-testid="error-message">{errors.message}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl disabled:translate-y-0 disabled:opacity-60"
                  >
                    {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : <><Send className="h-4 w-4" /> Send Message</>}
                  </button>
                  <p className="text-center text-xs text-muted-foreground">We respect your privacy. Your details are never shared.</p>
                </form>
              )}
            </div>
          </Reveal>

          {/* Info */}
          <Reveal delay={0.12}>
            <div className="space-y-4">
              {/* Quick actions */}
              <div className="grid grid-cols-3 gap-3" data-testid="contact-quick-actions">
                <a
                  href={`https://wa.me/${company.whatsapp}?text=${encodeURIComponent(company.whatsappMsg)}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="quick-whatsapp"
                  className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-card p-4 text-center transition-all hover:-translate-y-0.5 hover:border-[#25D366] hover:shadow-md"
                >
                  <span className="grid h-10 w-10 place-items-center rounded-full bg-[#25D366]/10 text-[#25D366]"><MessageCircle className="h-5 w-5" /></span>
                  <span className="text-xs font-semibold">WhatsApp</span>
                </a>
                <a
                  href={`tel:${company.phoneHref}`}
                  data-testid="quick-call"
                  className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-card p-4 text-center transition-all hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-md"
                >
                  <span className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-primary"><Phone className="h-5 w-5" /></span>
                  <span className="text-xs font-semibold">Call</span>
                </a>
                <a
                  href={`mailto:${company.email}`}
                  data-testid="quick-email"
                  className="flex flex-col items-center gap-2 rounded-2xl border border-border bg-card p-4 text-center transition-all hover:-translate-y-0.5 hover:border-accent/50 hover:shadow-md"
                >
                  <span className="grid h-10 w-10 place-items-center rounded-full bg-accent/10 text-accent"><Mail className="h-5 w-5" /></span>
                  <span className="text-xs font-semibold">Email</span>
                </a>
              </div>

              {/* Phone with copy */}
              <div className="flex items-center justify-between gap-4 rounded-2xl border border-border bg-card p-5">
                <div className="flex items-center gap-4">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><Phone className="h-5 w-5" /></span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Call us</p>
                    <a href={`tel:${company.phoneHref}`} className="mt-1 block font-medium hover:text-primary">{company.phone}</a>
                  </div>
                </div>
                <button onClick={() => copy(company.phone, "phone")} data-testid="copy-phone" aria-label="Copy phone" className="grid h-9 w-9 place-items-center rounded-full border border-border transition-colors hover:border-primary hover:text-primary">
                  {copied === "phone" ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>

              {/* Email with copy */}
              <div className="flex items-center justify-between gap-4 rounded-2xl border border-border bg-card p-5">
                <div className="flex items-center gap-4">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-accent/10 text-accent"><Mail className="h-5 w-5" /></span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email us</p>
                    <a href={`mailto:${company.email}`} className="mt-1 block break-all font-medium hover:text-primary">{company.email}</a>
                  </div>
                </div>
                <button onClick={() => copy(company.email, "email")} data-testid="copy-email" aria-label="Copy email" className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-border transition-colors hover:border-primary hover:text-primary">
                  {copied === "email" ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>

              {/* Location */}
              <div className="flex items-start gap-4 rounded-2xl border border-border bg-card p-5">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><MapPin className="h-5 w-5" /></span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Visit us</p>
                  <p className="mt-1 font-medium">{company.address}</p>
                </div>
              </div>

              {/* Business hours */}
              <div className="rounded-2xl border border-border bg-card p-5" data-testid="business-hours">
                <div className="flex items-center gap-4">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-success/10 text-success"><Clock className="h-5 w-5" /></span>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Business hours</p>
                </div>
                <ul className="mt-4 space-y-2 text-sm">
                  {company.hoursList.map((h) => (
                    <li key={h.day} className="flex items-center justify-between border-b border-border pb-2 last:border-0 last:pb-0">
                      <span className="text-muted-foreground">{h.day}</span>
                      <span className={cn("font-medium", h.time === "Closed" && "text-destructive")}>{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="overflow-hidden rounded-2xl border border-border">
                <iframe
                  title="NeuroTriq TechLabs office location"
                  src="https://www.google.com/maps?q=New%20Delhi%2C%20India&output=embed"
                  className="h-56 w-full grayscale"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="contact-map"
                />
              </div>

              <div className="flex gap-3">
                {[{ icon: Linkedin, href: company.social.linkedin }, { icon: Github, href: company.social.github }, { icon: Twitter, href: company.social.twitter }].map((s, i) => (
                  <a key={i} href={s.href} target="_blank" rel="noreferrer" className="grid h-11 w-11 place-items-center rounded-full border border-border bg-card transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground">
                    <s.icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        {/* Trust badges */}
        <div className="mx-auto mt-16 grid max-w-7xl grid-cols-2 gap-6 px-5 sm:px-8 md:grid-cols-3 lg:grid-cols-6">
          {trustBadges.map((b) => (
            <motion.div key={b.label} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="flex items-center gap-3 text-sm">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-success/10 text-success"><Icon name={b.icon} className="h-4 w-4" /></span>
              <span className="font-medium">{b.label}</span>
            </motion.div>
          ))}
        </div>
      </section>
    </>
  );
}
